import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { arrayToCSV, maskEmail, devError } from "../../utils/security";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryClient";
import { formatDate, formatDateTime } from "../../lib/format";
import { useOrganization } from "../../lib/organizationContext";
import AffiliateHeader from "../../components/affiliate/AffiliateHeader";
import { DataTable } from "../../components/DataTable";
import {
  Surface,
  Button,
  SearchField,
  Select,
  ListBox,
  Tabs,
  Chip,
  Alert,
  Skeleton,
} from "@heroui/react";
import DollarSign from "~icons/solar/dollar-minimalistic-bold";
import Clock from "~icons/solar/clock-circle-bold";
import Copy from "~icons/solar/copy-bold";
import Check from "~icons/solar/check-bold";
import Bell from "~icons/solar/bell-bold";
import UserPlus from "~icons/solar/user-plus-bold";
import ShoppingCart from "~icons/solar/cart-large-bold";
import X from "~icons/solar/close-bold";
import TrendingUp from "~icons/solar/graph-up-bold";
import Users from "~icons/solar/users-group-rounded-bold";
import Download from "~icons/solar/download-bold";
import Activity from "~icons/solar/pulse-bold";
import AlertCircle from "~icons/solar/danger-circle-bold";
import ExternalLink from "~icons/solar/square-top-down-bold";

interface Stats {
  totalEarnings: number;
  pendingEarnings: number;
  availableBalance: number;
  totalClicks: number;
  totalSignups: number;
  totalConversions: number;
  conversionRate: number;
}

interface Signup {
  id: string;
  date: string;
  maskedUserId: string;
  isNewUser: boolean;
}

interface Conversion {
  id: string;
  date: string;
  amount: number;
  commission: number;
  status: string;
  customerEmail: string;
}

interface Notification {
  id: string;
  type: "signup" | "purchase";
  message: string;
  timestamp: string;
  read: boolean;
}

interface ClickData {
  id: string;
  created_at: string;
  referrer: string;
  destination_url: string;
}

interface DashboardData {
  stats: Stats;
  notifications: Notification[];
}

const EMPTY_DASHBOARD_DATA: DashboardData = {
  stats: {
    totalEarnings: 0,
    pendingEarnings: 0,
    availableBalance: 0,
    totalClicks: 0,
    totalSignups: 0,
    totalConversions: 0,
    conversionRate: 0,
  },
  notifications: [],
};

const ACTIVITY_PAGE_SIZE = 10;

export default function ProfessionalDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showNotifications, setShowNotifications] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "signups" | "purchases" | "clicks"
  >("purchases");
  const { data: orgData = null } = useOrganization();

  // Search and Filter state
  const [signupStatusFilter, setSignupStatusFilter] = useState<string>("all");
  const [purchaseSearch, setPurchaseSearch] = useState("");
  const [purchaseStatusFilter, setPurchaseStatusFilter] =
    useState<string>("all");

  // Pagination state for the three activity tabs. Each tab fetches only
  // its own current page, never the affiliate's full click/signup/
  // purchase history at once.
  const [clicksPage, setClicksPage] = useState(1);
  const [signupsPage, setSignupsPage] = useState(1);
  const [purchasesPage, setPurchasesPage] = useState(1);
  useEffect(() => {
    setClicksPage(1);
    setSignupsPage(1);
    setPurchasesPage(1);
  }, [activeTab]);
  useEffect(() => setSignupsPage(1), [signupStatusFilter]);
  useEffect(() => setPurchasesPage(1), [purchaseStatusFilter]);

  const dashboardQuery = useQuery({
    queryKey: queryKeys.dashboardStats(user?.affiliateProgramId ?? ""),
    enabled: !!user?.affiliateId,
    queryFn: async (): Promise<DashboardData> => {
      const { data, error } = await supabase.rpc("get_affiliate_dashboard", {
        p_affiliate_program_id: user?.affiliateProgramId ?? null,
        p_affiliate_code: user?.affiliateCode ?? "",
        p_organization_id: user?.organizationId ?? null,
      });
      if (error) throw error;

      const referralsData: any[] = data.referrals ?? [];

      // Real signups come from affiliate_attributions (written by
      // track-turnover), not from referrals/conversions. The RPC doesn't
      // carry this, so just the count is fetched directly for the stat
      // tile, the full list lives in its own paginated query below.
      const { count: totalSignups } = await supabase
        .from("affiliate_attributions_public")
        .select("id", { count: "exact", head: true })
        .eq("affiliate_program_id", user?.affiliateProgramId ?? "");

      // Notifications (using a handful of recent referrals as the
      // notification source, not the full purchases list).
      const notifications: Notification[] = referralsData.slice(0, 7).map((ref) => ({
        id: ref.id,
        type: "purchase" as const,
        message: `New purchase: $${parseFloat(ref.amount).toFixed(2)}`,
        timestamp: new Date(ref.conversion_date || ref.created_at).toISOString(),
        read: false,
      }));

      return {
        stats: { ...data.stats, totalSignups: totalSignups ?? 0 },
        notifications,
      };
    },
  });

  const clicksQuery = useQuery({
    queryKey: queryKeys.affiliateActivity(user?.affiliateProgramId ?? "", "clicks", clicksPage),
    enabled: !!user?.affiliateId && activeTab === "clicks",
    queryFn: async () => {
      const from = (clicksPage - 1) * ACTIVITY_PAGE_SIZE;
      const { data, count, error } = await supabase
        .from("clicks")
        .select("id, created_at, referrer, destination_url", { count: "exact" })
        .eq("affiliate_program_id", user?.affiliateProgramId ?? "")
        .order("created_at", { ascending: false })
        .range(from, from + ACTIVITY_PAGE_SIZE - 1);
      if (error) throw error;
      const rows: ClickData[] = (data ?? []).map((c: any) => ({
        ...c,
        referrer: c.referrer || "direct",
      }));
      return { rows, total: count ?? 0 };
    },
  });
  const clicks = clicksQuery.data?.rows ?? [];
  const clicksTotal = clicksQuery.data?.total ?? 0;
  const clicksPageCount = Math.max(1, Math.ceil(clicksTotal / ACTIVITY_PAGE_SIZE));

  const signupsQuery = useQuery({
    queryKey: [
      ...queryKeys.affiliateActivity(user?.affiliateProgramId ?? "", "signups", signupsPage),
      signupStatusFilter,
    ],
    enabled: !!user?.affiliateId && activeTab === "signups",
    queryFn: async () => {
      const from = (signupsPage - 1) * ACTIVITY_PAGE_SIZE;
      // Reads the masked view: the real external_user_id is masked at
      // the database layer, it never reaches this client at all.
      let query = supabase
        .from("affiliate_attributions_public")
        .select("id, masked_user_id, is_new_user, linked_at", { count: "exact" })
        .eq("affiliate_program_id", user?.affiliateProgramId ?? "");
      if (signupStatusFilter === "new") {
        query = query.eq("is_new_user", true);
      } else if (signupStatusFilter === "existing") {
        query = query.eq("is_new_user", false);
      }
      const { data, count, error } = await query
        .order("linked_at", { ascending: false })
        .range(from, from + ACTIVITY_PAGE_SIZE - 1);
      if (error) throw error;
      const rows: Signup[] = (data ?? []).map((a) => ({
        id: a.id,
        date: a.linked_at,
        maskedUserId: a.masked_user_id,
        isNewUser: a.is_new_user,
      }));
      return { rows, total: count ?? 0 };
    },
  });
  const signups = signupsQuery.data?.rows ?? [];
  const signupsTotal = signupsQuery.data?.total ?? 0;
  const signupsPageCount = Math.max(1, Math.ceil(signupsTotal / ACTIVITY_PAGE_SIZE));

  const purchasesQuery = useQuery({
    queryKey: [
      ...queryKeys.affiliateActivity(user?.affiliateProgramId ?? "", "purchases", purchasesPage),
      purchaseStatusFilter,
    ],
    enabled: !!user?.affiliateId && activeTab === "purchases",
    queryFn: async () => {
      const from = (purchasesPage - 1) * ACTIVITY_PAGE_SIZE;
      let query = supabase
        .from("referrals")
        .select("id, conversion_date, amount, commission_amount, status, external_user_id", { count: "exact" })
        .eq("affiliate_program_id", user?.affiliateProgramId ?? "");
      if (purchaseStatusFilter !== "all") {
        query = query.eq("status", purchaseStatusFilter);
      }
      const { data, count, error } = await query
        .order("conversion_date", { ascending: false })
        .range(from, from + ACTIVITY_PAGE_SIZE - 1);
      if (error) throw error;
      const rows: Conversion[] = (data ?? []).map((ref) => ({
        id: ref.id,
        date: ref.conversion_date,
        amount: parseFloat(ref.amount as any) || 0,
        commission: parseFloat(ref.commission_amount as any) || 0,
        status: ref.status,
        customerEmail: ref.external_user_id,
      }));
      return { rows, total: count ?? 0 };
    },
  });
  const conversions = purchasesQuery.data?.rows ?? [];
  const conversionsTotal = purchasesQuery.data?.total ?? 0;
  const purchasesPageCount = Math.max(1, Math.ceil(conversionsTotal / ACTIVITY_PAGE_SIZE));

  const dashboardData = user?.affiliateId
    ? dashboardQuery.data ?? EMPTY_DASHBOARD_DATA
    : EMPTY_DASHBOARD_DATA;
  const loading = !!user?.affiliateId && dashboardQuery.isLoading;
  const { stats, notifications } = dashboardData;

  // Use custom referral URL if set by admin, otherwise fall back to the
  // organization's actual product/app URL (base_url) -- not app_url,
  // which is the affiliate dashboard's own domain, not the product
  // being promoted.
  const affiliateLink = user
    ? user.customReferralUrl
      ? user.customReferralUrl.replace(
          "{affiliate_code}",
          user.affiliateCode || ""
        )
      : `${orgData?.base_url || orgData?.app_url || window.location.origin}?ref=${user.affiliateCode}`
    : "";
  const unreadCount = notifications.filter((n) => !n.read).length;

  // signupStatusFilter and purchaseStatusFilter are both applied
  // server-side in their respective queries now, so pagination counts
  // stay correct. Only the current page is ever loaded, so
  // purchaseSearch filters just that page.
  const filteredSignups = signups;
  const filteredConversions = conversions.filter((conversion) =>
    conversion.customerEmail.toLowerCase().includes(purchaseSearch.toLowerCase())
  );

  // Export fetches the complete filtered set, not just the currently
  // loaded page (EXPORT_LIMIT caps it as a safety net, not a design
  // choice: an export is a deliberate action, browsing is paginated).
  const EXPORT_LIMIT = 5000;

  const exportSignupsToCSV = async () => {
    let query = supabase
      .from("affiliate_attributions_public")
      .select("linked_at, masked_user_id, is_new_user")
      .eq("affiliate_program_id", user?.affiliateProgramId ?? "");
    if (signupStatusFilter === "new") {
      query = query.eq("is_new_user", true);
    } else if (signupStatusFilter === "existing") {
      query = query.eq("is_new_user", false);
    }
    const { data, error } = await query
      .order("linked_at", { ascending: false })
      .limit(EXPORT_LIMIT);
    if (error) {
      devError("Error exporting signups:", error);
      return;
    }
    const headers = ["Date", "User", "Type"];
    const rows = (data ?? []).map((s) => [
      formatDate(s.linked_at),
      s.masked_user_id,
      s.is_new_user ? "New signup" : "Existing user",
    ]);
    const csv = arrayToCSV(headers, rows);
    downloadCSV(csv, "signups");
  };

  const exportPurchasesToCSV = async () => {
    let query = supabase
      .from("referrals")
      .select("conversion_date, external_user_id, amount, commission_amount, status")
      .eq("affiliate_program_id", user?.affiliateProgramId ?? "");
    if (purchaseStatusFilter !== "all") {
      query = query.eq("status", purchaseStatusFilter);
    }
    const { data, error } = await query
      .order("conversion_date", { ascending: false })
      .limit(EXPORT_LIMIT);
    if (error) {
      devError("Error exporting purchases:", error);
      return;
    }
    const filtered = (data ?? []).filter((c) =>
      (c.external_user_id ?? "").toLowerCase().includes(purchaseSearch.toLowerCase())
    );
    const headers = ["Date", "Customer", "Sale Amount", "Commission", "Status"];
    const rows = filtered.map((c) => [
      formatDate(c.conversion_date),
      maskEmail(c.external_user_id),
      (parseFloat(c.amount as any) || 0).toFixed(2),
      (parseFloat(c.commission_amount as any) || 0).toFixed(2),
      c.status,
    ]);
    const csv = arrayToCSV(headers, rows);
    downloadCSV(csv, "purchases");
  };

  const downloadCSV = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Real-time subscription for new/updated conversions and program stats.
  // Rather than hand-patching 4 separate pieces of local state per event
  // (the previous approach -- error-prone, and only this page's own state
  // ever saw the update), invalidate the query: react-query refetches once
  // and every component reading dashboardStats picks up the fresh data
  // from the one shared cache entry, same pattern as AdminLayout's
  // notifications subscription.
  useEffect(() => {
    if (!user?.affiliateProgramId) return;

    const invalidate = () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardStats(user.affiliateProgramId!),
      });
      // Prefix match: every page/kind of the purchases/signups/clicks
      // activity queries for this affiliate, not just the current one.
      queryClient.invalidateQueries({
        queryKey: ["affiliate-activity", user.affiliateProgramId!],
      });
    };

    const referralsChannel = supabase
      .channel("affiliate-referrals")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "referrals",
          filter: `affiliate_program_id=eq.${user.affiliateProgramId}`,
        },
        invalidate
      )
      .subscribe();

    const updatesChannel = supabase
      .channel("affiliate-referrals-updates")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "referrals",
          filter: `affiliate_program_id=eq.${user.affiliateProgramId}`,
        },
        invalidate
      )
      .subscribe();

    const programChannel = supabase
      .channel("affiliate-program-stats")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "affiliate_programs",
          filter: `id=eq.${user.affiliateProgramId}`,
        },
        invalidate
      )
      .subscribe();

    return () => {
      referralsChannel.unsubscribe();
      updatesChannel.unsubscribe();
      programChannel.unsubscribe();
    };
  }, [user?.affiliateProgramId, queryClient]);


  const copyLink = () => {
    if (orgData?.name === "rootcause") {
      navigator.clipboard.writeText(user?.affiliateCode || "");
    } else {
      navigator.clipboard.writeText(affiliateLink);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const getSignupTypeColor = (isNewUser: boolean): 'success' | 'accent' => {
    return isNewUser ? 'success' : 'accent'
  }

  const getConversionStatusColor = (status: string): 'success' | 'warning' | 'default' => {
    switch (status) {
      case 'completed': return 'success'
      case 'pending': return 'warning'
      default: return 'default'
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <AffiliateHeader
        notifications={notifications}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
      />

      {/* Notifications Panel */}
      <AnimatePresence>
        {showNotifications && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setShowNotifications(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="fixed top-20 right-4 w-96 z-50"
            >
              <Surface variant="default" className="rounded-xl overflow-hidden">
                <div className="p-4 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
                  <h3 className="font-semibold text-foreground">Notifications</h3>
                  <button
                    onClick={() => setShowNotifications(false)}
                    className="text-muted hover:text-foreground transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-12 text-center">
                      <Bell className="h-10 w-10 text-muted mx-auto mb-3" />
                      <p className="text-muted">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${
                          !notification.read ? "bg-accent-soft/40" : ""
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-surface-secondary rounded-xl">
                            {notification.type === "signup" ? (
                              <UserPlus className="h-4 w-4 text-accent" />
                            ) : (
                              <ShoppingCart className="h-4 w-4 text-success" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground font-medium leading-snug">
                              {notification.message}
                            </p>
                            <p className="text-xs text-muted mt-1">
                              {formatDateTime(notification.timestamp)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="p-3 border-t border-black/10 dark:border-white/10">
                    <button
                      onClick={() =>
                        queryClient.setQueryData<DashboardData>(
                          queryKeys.dashboardStats(user?.affiliateProgramId ?? ""),
                          (prev) =>
                            prev && {
                              ...prev,
                              notifications: prev.notifications.map((n) => ({ ...n, read: true })),
                            }
                        )
                      }
                      className="w-full text-sm font-medium text-accent hover:underline"
                    >
                      Mark all as read
                    </button>
                  </div>
                )}
              </Surface>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 sm:py-8">
        {/* Suspension Warning Banner */}
        {user?.status === "suspended" && (
          <div className="mb-6">
            <Alert status="danger">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Account Suspended</Alert.Title>
                <Alert.Description>
                  Your affiliate account has been suspended. Your referral links
                  are currently inactive and will not track conversions. Please
                  contact support for assistance.
                </Alert.Description>
              </Alert.Content>
            </Alert>
          </div>
        )}

        {user?.status === "pending" && (
          <div className="mb-6">
            <Alert status="warning">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Application Pending</Alert.Title>
                <Alert.Description>
                  Your affiliate application is under review. You'll be notified
                  once it's approved.
                </Alert.Description>
              </Alert.Content>
            </Alert>
          </div>
        )}

        {user?.status === "rejected" && (
          <div className="mb-6">
            <Alert status="danger">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Application Not Approved</Alert.Title>
                <Alert.Description>
                  Unfortunately, your affiliate application was not approved.
                  Please contact support for more information.
                </Alert.Description>
                <button
                  onClick={logout}
                  className="mt-2 text-sm font-medium text-danger hover:underline"
                >
                  Sign Out
                </button>
              </Alert.Content>
            </Alert>
          </div>
        )}

        {user?.status === "unauthorized" && (
          <div className="mb-6">
            <Alert status="danger">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Not an Affiliate Here</Alert.Title>
                <Alert.Description>
                  This account isn't an affiliate for this app. If you're trying to
                  access a different program, sign in from that program's own link
                  instead.
                </Alert.Description>
                <button
                  onClick={logout}
                  className="mt-2 text-sm font-medium text-danger hover:underline"
                >
                  Sign Out
                </button>
              </Alert.Content>
            </Alert>
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Welcome back, {user?.name}
          </h2>
          <p className="text-muted">Here's your performance overview</p>
        </div>

        {/* Affiliate Link Card */}
        <Surface variant="default" className="rounded-xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-2 bg-surface-secondary rounded-lg shrink-0">
                <Copy className="h-4 w-4 text-muted" />
              </div>
              <label className="font-semibold text-foreground whitespace-nowrap">
                Your Affiliate {orgData?.name === "rootcause" ? "Code" : "Link"}
              </label>
            </div>
            {user?.commissionRate && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-success-soft rounded-lg w-fit">
                <TrendingUp className="h-3.5 w-3.5 text-success-soft-foreground shrink-0" />
                <span className="text-xs font-semibold text-success-soft-foreground whitespace-nowrap">
                  {user.commissionRate}% Commission
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <div className="flex-1 min-h-10 md:min-h-9 px-4 py-2 flex items-center bg-surface-secondary rounded-xl">
              <code className="text-foreground font-mono text-sm break-all">
                {orgData?.name === "rootcause"
                  ? user?.affiliateCode
                  : affiliateLink}
              </code>
            </div>
            <Button onPress={copyLink}>
              {linkCopied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy {orgData?.name === "rootcause" ? "Code" : "Link"}
                </>
              )}
            </Button>
          </div>
          {user?.customReferralUrl && (
            <p className="mt-3 text-xs text-muted">
              This is a custom referral link configured by your administrator
            </p>
          )}
        </Surface>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {/* Available Balance */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <DollarSign className="h-5 w-5 text-success" />
              </div>
            </div>
            <p className="text-sm font-medium text-muted mb-1">Available</p>
            {loading ? (
              <Skeleton className="h-8 w-20 rounded mb-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mb-1">
                ${stats?.availableBalance.toFixed(2)}
              </p>
            )}
            <p className="text-xs text-muted">Ready to withdraw</p>
          </Surface>

          {/* Pending Earnings */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <Clock className="h-5 w-5 text-warning" />
              </div>
            </div>
            <p className="text-sm font-medium text-muted mb-1">Pending</p>
            {loading ? (
              <Skeleton className="h-8 w-20 rounded mb-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mb-1">
                ${stats?.pendingEarnings.toFixed(2)}
              </p>
            )}
            <p className="text-xs text-muted">Processing period</p>
          </Surface>

          {/* Signups */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <UserPlus className="h-5 w-5 text-accent" />
              </div>
            </div>
            <p className="text-sm font-medium text-muted mb-1">Signups</p>
            {loading ? (
              <Skeleton className="h-8 w-12 rounded mb-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mb-1">
                {stats?.totalSignups}
              </p>
            )}
            <p className="text-xs text-muted">Total referrals</p>
          </Surface>

          {/* Purchases */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <ShoppingCart className="h-5 w-5 text-foreground" />
              </div>
            </div>
            <p className="text-sm font-medium text-muted mb-1">Purchases</p>
            {loading ? (
              <Skeleton className="h-8 w-12 rounded mb-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mb-1">
                {stats?.totalConversions}
              </p>
            )}
            <p className="text-xs text-muted">Total conversions</p>
          </Surface>

          {/* Clicks */}
          <Surface variant="default" className="rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2.5 bg-surface-secondary rounded-xl">
                <Activity className="h-5 w-5 text-accent" />
              </div>
            </div>
            <p className="text-sm font-medium text-muted mb-1">Clicks</p>
            {loading ? (
              <Skeleton className="h-8 w-12 rounded mb-1" />
            ) : (
              <p className="text-2xl font-bold text-foreground mb-1">
                {stats?.totalClicks}
              </p>
            )}
            <p className="text-xs text-muted">Total link clicks</p>
          </Surface>
        </div>

        {/* Activity Section */}
        <Surface variant="default" className="rounded-xl overflow-hidden">
          <div className="px-2 py-4 sm:p-4 border-b border-black/10 dark:border-white/10">
            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as any)}>
              <Tabs.ListContainer>
                <Tabs.List aria-label="Activity sections">
                  <Tabs.Tab id="clicks" className="gap-2 !w-auto">
                    <Activity className="h-4 w-4" />
                    Clicks
                    <Tabs.Indicator />
                  </Tabs.Tab>
                  <Tabs.Tab id="signups" className="gap-2 !w-auto">
                    <UserPlus className="h-4 w-4" />
                    Signups
                    <Tabs.Indicator />
                  </Tabs.Tab>
                  <Tabs.Tab id="purchases" className="gap-2 !w-auto">
                    <ShoppingCart className="h-4 w-4" />
                    Purchases
                    <Tabs.Indicator />
                  </Tabs.Tab>
                </Tabs.List>
              </Tabs.ListContainer>
            </Tabs>
          </div>

          {/* Clicks Content */}
          {activeTab === "clicks" && (
            <div className="p-6">
              {clicksTotal === 0 && (stats?.totalClicks || 0) > 0 && (
                <div className="mb-4 text-xs text-warning-soft-foreground bg-warning-soft px-3 py-1.5 rounded-lg flex items-center gap-1 w-fit">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>
                    Historical clicks tracked before detailed logging was
                    enabled
                  </span>
                </div>
              )}

              <Surface variant="secondary" className="rounded-2xl overflow-hidden">
                <DataTable
                  ariaLabel="Recent clicks"
                  className="min-w-full"
                  columns={[
                    { key: "date", header: "Date & Time", isRowHeader: true },
                    { key: "source", header: "Source" },
                    { key: "destination", header: "Destination" },
                  ]}
                  rows={clicks}
                  getRowId={(click) => click.id}
                  isLoading={clicksQuery.isLoading}
                  emptyMessage={
                    <div className="py-12 text-center">
                      <Activity className="h-10 w-10 text-muted mx-auto mb-2" />
                      <p className="text-muted">No click activity yet</p>
                      <p className="text-xs text-muted mt-1">Share your affiliate link to start tracking</p>
                    </div>
                  }
                  pagination={{
                    page: clicksPage,
                    pageCount: clicksPageCount,
                    total: clicksTotal,
                    pageSize: ACTIVITY_PAGE_SIZE,
                    onPageChange: setClicksPage,
                    itemLabel: "clicks",
                  }}
                  renderRow={(click) => [
                    <span className="text-sm text-muted">{formatDateTime(click.created_at)}</span>,
                    click.referrer === "direct" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface text-muted rounded-md text-xs font-medium">
                        <ExternalLink className="h-3 w-3" />
                        Direct
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 bg-accent-soft text-accent-soft-foreground rounded-md text-xs font-medium"
                        title={click.referrer}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {click.referrer.length > 30 ? click.referrer.substring(0, 30) + "..." : click.referrer}
                      </span>
                    ),
                    <span className="text-sm text-muted">{click.destination_url}</span>,
                  ]}
                />
              </Surface>

              <Surface variant="secondary" className="mt-6 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-surface rounded-lg">
                    <Activity className="h-4 w-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">
                      Click Tracking
                    </p>
                    <p className="text-sm text-muted">
                      Every visitor who clicks your affiliate link is tracked
                      here. When they make a purchase, it converts to
                      commission.
                    </p>
                  </div>
                </div>
              </Surface>
            </div>
          )}

          {/* Signups Content */}
          {activeTab === "signups" && (
            <div className="p-6">
              {/* Filter Controls: no search, a signup event carries no
                  identifying field to search by. */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Select
                  aria-label="Filter by status"
                  variant="secondary"
                  value={signupStatusFilter}
                  onChange={(v) => setSignupStatusFilter(v as string)}
                  className="w-full sm:w-48"
                >
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      <ListBox.Item id="all" textValue="All Status">
                        All Status
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                      <ListBox.Item id="new" textValue="New signup">
                        New signup
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                      <ListBox.Item id="existing" textValue="Existing user">
                        Existing user
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    </ListBox>
                  </Select.Popover>
                </Select>
                <Button variant="outline" onPress={exportSignupsToCSV}>
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </div>

              <Surface variant="secondary" className="rounded-2xl overflow-hidden">
                <DataTable
                  ariaLabel="Signups"
                  className="min-w-full"
                  columns={[
                    { key: "date", header: "Date", isRowHeader: true },
                    { key: "user", header: "User" },
                    { key: "type", header: "Type" },
                  ]}
                  rows={filteredSignups}
                  getRowId={(signup) => signup.id}
                  isLoading={signupsQuery.isLoading}
                  emptyMessage={
                    <div className="py-12 text-center">
                      <Users className="h-10 w-10 text-muted mx-auto mb-2" />
                      <p className="text-muted">No signups found</p>
                    </div>
                  }
                  pagination={{
                    page: signupsPage,
                    pageCount: signupsPageCount,
                    total: signupsTotal,
                    pageSize: ACTIVITY_PAGE_SIZE,
                    onPageChange: setSignupsPage,
                    itemLabel: "signups",
                  }}
                  renderRow={(signup) => [
                    <span className="text-sm text-muted">{formatDate(signup.date)}</span>,
                    <span className="text-sm font-mono text-muted">{signup.maskedUserId}</span>,
                    <Chip color={getSignupTypeColor(signup.isNewUser)} size="sm">
                      {signup.isNewUser ? "New signup" : "Existing user"}
                    </Chip>,
                  ]}
                />
              </Surface>

              <Surface variant="secondary" className="mt-6 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-surface rounded-lg">
                    <Activity className="h-4 w-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">
                      Commission Policy
                    </p>
                    <p className="text-sm text-muted">
                      Signups don't earn commission. You earn commission only
                      when referred users make a purchase.
                    </p>
                  </div>
                </div>
              </Surface>
            </div>
          )}

          {/* Purchases Content */}
          {activeTab === "purchases" && (
            <div className="p-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <SearchField aria-label="Search purchases" variant="secondary" className="flex-1" value={purchaseSearch} onChange={setPurchaseSearch}>
                  <SearchField.Group>
                    <SearchField.SearchIcon />
                    <SearchField.Input className="w-full" placeholder="Search purchases..." />
                    <SearchField.ClearButton />
                  </SearchField.Group>
                </SearchField>
                <Select
                  aria-label="Filter by status"
                  variant="secondary"
                  value={purchaseStatusFilter}
                  onChange={(v) => setPurchaseStatusFilter(v as string)}
                  className="w-full sm:w-48"
                >
                  <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover>
                    <ListBox>
                      <ListBox.Item id="all" textValue="All Status">
                        All Status
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                      <ListBox.Item id="completed" textValue="Completed">
                        Completed
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                      <ListBox.Item id="pending" textValue="Pending">
                        Pending
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    </ListBox>
                  </Select.Popover>
                </Select>
                <Button variant="outline" onPress={exportPurchasesToCSV}>
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </div>

              <Surface variant="secondary" className="rounded-2xl overflow-hidden">
                <DataTable
                  ariaLabel="Purchases"
                  className="min-w-full"
                  columns={[
                    { key: "date", header: "Date", isRowHeader: true },
                    { key: "customer", header: "Customer" },
                    { key: "sale", header: "Sale", className: "text-right" },
                    { key: "commission", header: "Commission", className: "text-right" },
                    { key: "status", header: "Status" },
                  ]}
                  rows={filteredConversions}
                  getRowId={(conversion) => conversion.id}
                  isLoading={purchasesQuery.isLoading}
                  emptyMessage={
                    <div className="py-12 text-center">
                      <ShoppingCart className="h-10 w-10 text-muted mx-auto mb-2" />
                      <p className="text-muted">No purchases found</p>
                    </div>
                  }
                  pagination={{
                    page: purchasesPage,
                    pageCount: purchasesPageCount,
                    total: conversionsTotal,
                    pageSize: ACTIVITY_PAGE_SIZE,
                    onPageChange: setPurchasesPage,
                    itemLabel: "purchases",
                  }}
                  renderRow={(conversion) => [
                    <span className="text-sm text-muted">{formatDate(conversion.date)}</span>,
                    <span className="text-sm text-foreground font-mono" title="Customer email masked for privacy">
                      {maskEmail(conversion.customerEmail)}
                    </span>,
                    <span className="text-sm text-foreground font-medium block text-right">
                      ${conversion.amount.toFixed(2)}
                    </span>,
                    <span className="text-sm text-success font-semibold block text-right">
                      +${conversion.commission.toFixed(2)}
                    </span>,
                    <Chip color={getConversionStatusColor(conversion.status)} size="sm">
                      {conversion.status}
                    </Chip>,
                  ]}
                />
              </Surface>

              <Surface variant="secondary" className="mt-6 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-surface rounded-lg">
                    <DollarSign className="h-4 w-4 text-success" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">
                      How You Earn
                    </p>
                    <p className="text-sm text-muted">
                      You earn commission when referred users make a purchase or
                      subscribe to a paid plan.
                    </p>
                  </div>
                </div>
              </Surface>
            </div>
          )}
        </Surface>
      </main>
    </div>
  );
}
